import moment from 'moment'
import { TREATMENT_FIXTURE, TreatmentCard } from './treatmentFixture'

/**
 * Unscheduled treatment, split by how long ago the case was planned.
 *
 * "Planned", not "presented": the date is `lastTreatmentPlanDate`, the plan's
 * creation in the PMS, which records no presented date. A new patient's plan
 * is made when their first visit is booked, before anyone has seen them.
 *
 * Same reasoning as the hygiene bands: one pile of everything unbooked is
 * unfinishable, and how stale the conversation is changes what the call has to
 * be. Somebody presented a fortnight ago is being reminded; somebody presented
 * six months ago is being re-presented, because the quote is stale, the mouth
 * has moved on, and pretending it is the same conversation is what makes those
 * calls land badly.
 *
 * The boundaries are 30 and 90 days. Both are chosen from the shape of the
 * practice's own plan record rather than picked out of the air: plans
 * run at a fairly even 190 to 310 plans a month across 2026, so 30/90 cuts the
 * year into roughly 300, 600 and 830 plans. Each band is a few hundred cases,
 * which is the size the hygiene split showed is workable.
 *
 * Read the caveat on `TREATMENT_TOTALS` before treating the third band's depth
 * as real: the cohort behind this module stops at 2026, and the practice's plan
 * history runs back to 2020.
 */
export type TreatmentBandId = `fresh` | `cooling` | `cold` | `stale`

export type TreatmentBand = {
  id: TreatmentBandId
  /** Full name, for the band's own page. */
  name: string
  /** Name inside the bundle, where "unscheduled treatment" is already said. */
  shortName: string
  strapline: string
  /** Inclusive lower bound in days since the plan was made. */
  fromDays: number
  /** Inclusive upper bound, or undefined for the open-ended tail. */
  toDays?: number
}

export const TREATMENT_BANDS: TreatmentBand[] = [
  {
    id: `fresh`,
    name: `Planned in the last 30 days`,
    shortName: `0 to 30 days`,
    strapline: `They remember the conversation. The easiest of these to close.`,
    fromDays: 0,
    toDays: 30,
  },
  {
    id: `cooling`,
    name: `Planned 31 to 90 days ago`,
    shortName: `31 to 90 days`,
    strapline: `Long enough to have drifted, recent enough that the plan still stands.`,
    fromDays: 31,
    toDays: 90,
  },
  {
    id: `cold`,
    name: `Planned 91 to 365 days ago`,
    shortName: `91 to 365 days`,
    strapline: `Old enough that the case needs presenting again, not chasing.`,
    fromDays: 91,
    toDays: 365,
  },
  {
    id: `stale`,
    name: `Planned more than 365 days ago`,
    shortName: `Over 365 days`,
    strapline: `Planned work nobody booked, some of it a year old and some far older. Shown because it is real production, not because it is today's calling list.`,
    fromDays: 366,
  },
]

/**
 * The contact search that finds a band, so the API counts it rather than the
 * browser.
 *
 * `unbookedTreatmentValue` is the practice's own rollup of treatment that is
 * PENDING with no live appointment, and `lastTreatmentPlanDate` is when they
 * were last made a plan. Between them the queue is an ordinary contact
 * search, the same shape as hygiene recall, which the API can count, sort and
 * page. That is what the rollup fields were asked for.
 *
 * Bracket ranges, not `>=`: the search rejects the comparison form with
 * "Unexpected error", and because a GraphQL error still returns HTTP 200 the
 * failure reads as an empty queue rather than as a fault.
 */
/**
 * Everyone the treatment pipeline covers, whatever band they fall in.
 *
 * The bands slice this by when the plan was made; membership is not a
 * band's business, so anything comparing the pipeline against the practice's
 * real population measures against this and never against one window.
 */
export const TREATMENT_POPULATION = `unbookedTreatmentValue:[1 TO *]`

export function bandSearchTerms(
  id: TreatmentBandId,
  now: moment.Moment = moment(),
  population: string = TREATMENT_POPULATION,
): string[] {
  const band = treatmentBand(id)
  const day = (daysAgo: number) => now.clone().subtract(daysAgo, `days`).format(`YYYY-MM-DD`)
  const value = population
  /**
   * The open-ended band is expressed as a negation, not as a range starting at
   * `*`.
   *
   * 490 of Hamblin's 4,499 people with outstanding treatment have no
   * `lastTreatmentPlanDate` at all, and a missing field never matches a range,
   * so a `[* TO x]` band silently loses them. Combining the range with
   * `OR NOT lastTreatmentPlanDate:*` does not work either: the search accepts
   * that form and returns zero rows, no error, which is the worst way to be
   * wrong. Inverting the recent range catches the old and the undated together,
   * and it reconciles: 1,836 dated plus 490 undated is the 2,326 this returns.
   */
  if (band.toDays === undefined) return [value, `NOT lastTreatmentPlanDate:[${day(band.fromDays - 1)} TO *]`]
  // Oldest bound first: `fromDays` is how long ago the band starts, so it is
  // the *later* date.
  return [value, `lastTreatmentPlanDate:[${day(band.toDays)} TO ${day(band.fromDays)}]`]
}

export function treatmentBand(id: TreatmentBandId): TreatmentBand {
  const band = TREATMENT_BANDS.find((candidate) => candidate.id === id)
  // The ids come from a closed union and from the registry's own queue ids, so
  // a miss is a wiring mistake rather than bad input.
  if (!band) throw new Error(`Unknown treatment band: ${id}`)
  return band
}

/** One option in the treatment board's band control. `id` undefined is every band at once. */
export type TreatmentBandOption = { id: TreatmentBandId | undefined; label: string }

/**
 * The board's band control, the whole cohort first, then `TREATMENT_BANDS`'
 * own order so the control reads freshest first, the same direction as the
 * bands.
 *
 * It leads the list but is not what the board opens on. See
 * `DEFAULT_TREATMENT_BAND`.
 *
 * "Any time" rather than "All": this control is now a menu whose trigger shows
 * only the chosen label, and a button reading "All" beside a locations filter
 * says nothing about what it is all of. Not "Any age" either, which was read
 * as the patient's age rather than the plan's; the picker carries the word
 * "Planned" beside whichever label is chosen, and "Planned: any time" is
 * the only one of the three that can be read exactly one way.
 */
export const TREATMENT_BAND_OPTIONS: TreatmentBandOption[] = [
  { id: undefined, label: `Any time` },
  ...TREATMENT_BANDS.map((band) => ({ id: band.id, label: band.shortName })),
]

/**
 * The band a `?band=` URL value names, or `undefined` ("All") when it does
 * not name one.
 *
 * A missing or unrecognised value both read as "All" rather than as an error.
 * The param is user-editable (typed, bookmarked, or shared from an older
 * build with different ids), so a value this build does not know has to widen
 * back to the whole board rather than silently show nobody.
 */
export function bandFromParam(value: string | null): TreatmentBandId | undefined {
  return TREATMENT_BANDS.some((band) => band.id === value) ? (value as TreatmentBandId) : undefined
}

/**
 * The band the board opens on when the URL names none.
 *
 * Every case with treatment outstanding is 4,559 people at Hamblin, and a
 * board that deep is a list, not a day's work. The freshest 30 days is the
 * slice worth opening on: those patients still remember the conversation, so
 * the call is a reminder rather than a re-presentation, and the whole cohort
 * is one click away on "All".
 */
export const DEFAULT_TREATMENT_BAND: TreatmentBandId = `fresh`

/**
 * The band a `?band=` value names, falling back to `DEFAULT_TREATMENT_BAND`
 * when the URL carries no band at all.
 *
 * Three cases, deliberately not two. A **missing** param means nobody has
 * chosen yet (the pipeline nav, a bare link), so the default applies. The
 * literal `all` means somebody chose the whole cohort, and has to survive a
 * reload, which is why the control writes it rather than stripping the param.
 * A value this build does not recognise widens to "All" for the reason
 * `bandFromParam` gives: showing everyone beats showing nobody.
 */
export function bandFromParamOrDefault(value: string | null): TreatmentBandId | undefined {
  if (value === null) return DEFAULT_TREATMENT_BAND
  return bandFromParam(value)
}

/**
 * Days since the practice last put an unbooked plan in front of this person.
 *
 * Takes the date rather than a card, because the same question is asked of a
 * live contact's `lastTreatmentPlanDate` — which arrives as a timestamp — and
 * of a staged card's date-only `presented`.
 */
export function daysSincePlanned(presented: string, now = moment()): number {
  // Date-only comparison, so it is made at day granularity whichever form the
  // value arrived in. Comparing against a timestamp would call a case planned
  // this morning "-1 days".
  return now.startOf(`day`).diff(moment(presented.slice(0, 10), `YYYY-MM-DD`).startOf(`day`), `days`)
}

export function bandForCard(card: TreatmentCard, now = moment()): TreatmentBandId {
  const age = daysSincePlanned(card.presented, now)
  // A plan date in the future is a data fault, not a band. It reads as
  // freshest rather than falling off the end, so nobody loses a case to it.
  if (age <= 30) return `fresh`
  if (age <= 90) return `cooling`
  if (age <= 365) return `cold`
  // Nothing is hidden. A plan from 2020 is still production the practice can
  // see, and a window that dropped it would quietly delete millions of dollars
  // from a number they are being shown.
  return `stale`
}

export function cardsInBand(
  id: TreatmentBandId,
  cards: TreatmentCard[] = TREATMENT_FIXTURE,
  now = moment(),
): TreatmentCard[] {
  // Cloned per card because `daysSincePlanned` calls `startOf('day')` on the
  // moment it is given, which mutates the caller's. The clock is injectable for
  // the same reason `bandForCard` takes one: without it a test that pins a date
  // is still measured against the real today, and it passes only on the day it
  // was written.
  return cards.filter((card) => bandForCard(card, now.clone()) === id)
}

/** Production sitting unbooked in a band. */
export function bandValue(id: TreatmentBandId, cards: TreatmentCard[] = TREATMENT_FIXTURE, now = moment()): number {
  return cardsInBand(id, cards, now).reduce((total, card) => total + card.value, 0)
}
